/**
 * Карточка клиента консультанта (AK-979) — контракт, запросы и чистые
 * помощники вкладок.
 *
 * Backend: `GET /api/v1/consultant/clients/{external_id}/card|visits|inspections|sales`
 * (`backend/src/ikolhoz/schemas/consultant_client_card.py`). Данные приходят
 * из бота продаж (Bitrix + 1С) через наш сервер; во внешнюю систему фронт не
 * ходит.
 *
 * Помощники вынесены из страницы, чтобы проверять тестом без браузера то, что
 * ломается молча: деньги без копеек, «0» вместо прочерка, непонятная ошибка
 * источника.
 */
import { api, ApiError } from '@/lib/api';
import { formatMoneyCell } from '@/lib/planning/money';
import type { ClientState } from './types';

// ─────────────────────────── контракт ───────────────────────────

export interface CardPerson {
  bitrix_user_id: number | null;
  name: string | null;
}

export interface CardContact {
  name: string;
  post: string;
  phones: string[];
}

export interface CardVisit {
  id: number | null;
  date: string | null;
  completed: boolean;
  manager: CardPerson;
  type_name: string | null;
  subject: string | null;
  location: string | null;
  text: string | null;
  agreed: string | null;
  next_step: string | null;
  source: string;
}

export interface PricePoint {
  price: number;
  qty: number | null;
}

export interface Prices {
  distinct: PricePoint[];
  avg: number | null;
  many: boolean;
}

export interface SalesComparisonLine {
  name: string | null;
  cur_qty: number;
  cur_amount: number;
  prev_qty: number;
  prev_amount: number;
  cur_prices: Prices;
  prev_prices: Prices;
}

export interface Unlabeled {
  orders: number;
  amount: number;
}

export interface BotInspection {
  id: number;
  date: string | null;
  created_at: string | null;
  field_name: string | null;
  crop: string | null;
  author: CardPerson;
  result_text: string | null;
  recommendations: string | null;
  report_type: string | null;
  gps: { lat: number; lng: number; source: string | null } | null;
  photos_count: number;
  source: string;
}

export interface ClientSystemState {
  state: ClientState;
  crm_company_id: string | null;
  linked_tenant_id: string | null;
  farm_name: string | null;
  cluster_id: string | null;
  cluster_name: string | null;
  department_path: string | null;
  fields_count: number;
}

export interface ClientCard {
  external_id: string;
  title: string | null;
  inn: string | null;
  manager: CardPerson;
  requisites: {
    client_status: string | null;
    is_priority: boolean;
    priority_letter: string | null;
    region: string | null;
    city: string | null;
    district: string | null;
    farm_size_category: string | null;
    crop_direction: string | null;
    on_control: string[];
  };
  area: {
    total_ha: number | null;
    segments: { crops: string | null; gardens: string | null; other: string | null };
    crops: { crop: string; ha: number }[];
  };
  goals: { label: string; text: string }[];
  contacts: {
    owner: CardContact[];
    head: CardContact[];
    agro: CardContact[];
    other: CardContact[];
  };
  meetings: { last: CardVisit[]; last_visit_at: string | null; planned: CardVisit[] };
  competitors: { date: string | null; author: string | null; competitor: string; quote: string }[];
  sales: {
    cur_season: string | null;
    prev_season: string | null;
    lines: SalesComparisonLine[];
    totals: { cur_amount: number; prev_amount: number };
    unlabeled: Unlabeled;
    shipments: null;
  };
  inspections: { total: number; last: BotInspection[] };
  not_in_source: Record<string, string>;
  synced_at: string | null;
  system: ClientSystemState;
}

interface Page {
  total: number;
  page: number;
  per_page: number;
}

export interface ClientVisits extends Page {
  state: 'done' | 'planned';
  items: CardVisit[];
}

export interface ClientInspections extends Page {
  items: BotInspection[];
}

export interface ClientSales extends Page {
  seasons: { season: string; orders: number; amount: number }[];
  unlabeled: Unlabeled;
  items: {
    name: string | null;
    season: string | null;
    qty: number;
    amount: number;
    prices: Prices;
  }[];
  season: string | null;
  shipments: null;
  not_in_source: Record<string, string>;
}

// ─────────────────────────── запросы ───────────────────────────

const base = (externalId: string) =>
  `/api/v1/consultant/clients/${encodeURIComponent(externalId)}`;

export const getClientCard = (externalId: string) =>
  api.get<ClientCard>(`${base(externalId)}/card`);

export const getClientVisits = (
  externalId: string,
  state: 'done' | 'planned',
  dateFrom?: string,
) => {
  const p = new URLSearchParams({ state, per_page: '100' });
  if (dateFrom) p.set('date_from', dateFrom);
  return api.get<ClientVisits>(`${base(externalId)}/visits?${p.toString()}`);
};

export const getClientBotInspections = (externalId: string) =>
  api.get<ClientInspections>(`${base(externalId)}/inspections?per_page=100`);

export const getClientSales = (externalId: string) =>
  api.get<ClientSales>(`${base(externalId)}/sales?per_page=200`);

// ─────────────────────────── вкладки ───────────────────────────

/** Порядок утверждён founder 21.10 (PR #1176) — не переставлять. */
export const CLIENT_CARD_TABS = [
  { value: 'overview', label: 'Обзор' },
  { value: 'spp', label: 'СПП' },
  { value: 'inspections', label: 'Осмотры' },
  { value: 'visits', label: 'Визиты' },
  { value: 'sales', label: 'Продажи' },
  { value: 'plan', label: 'План продаж' },
  { value: 'trials', label: 'Опыты' },
] as const;

export type ClientCardTab = (typeof CLIENT_CARD_TABS)[number]['value'];

export function parseClientCardTab(raw: unknown): ClientCardTab | undefined {
  return CLIENT_CARD_TABS.some((t) => t.value === raw) && raw !== 'overview'
    ? (raw as ClientCardTab)
    : undefined;
}

// ─────────────────────────── ошибки ───────────────────────────

export type CardErrorKind = 'not_found' | 'source_down' | 'other';

/**
 * Три разных состояния, которые человек должен различать:
 * 404 — данных в CRM по клиенту нет (повтор не поможет);
 * 502/503 — бот не ответил или ответил не по контракту (повтор поможет);
 * прочее — сеть/права.
 */
export function cardErrorKind(e: unknown): CardErrorKind {
  if (e instanceof ApiError) {
    if (e.status === 404) return 'not_found';
    if (e.status === 502 || e.status === 503) return 'source_down';
  }
  return 'other';
}

export const CARD_ERROR_TEXT: Record<CardErrorKind, string> = {
  not_found: 'Нет данных из CRM по этому клиенту.',
  source_down:
    'Источник данных (бот продаж) сейчас не отвечает. Ничего не потеряно — повторите через минуту.',
  other: 'Не удалось загрузить данные. Проверьте связь и повторите.',
};

// ─────────────────────────── формат ───────────────────────────

/** Деньги — всегда два знака (правило founder 21.08): «1 240 000,00». */
export const formatRub = (value: number | null | undefined): string => formatMoneyCell(value);

/** Гектары — как в карточке Bitrix, два знака: «1 200,00». */
export const formatHa = (value: number | null | undefined): string => formatMoneyCell(value);

const QTY = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 3 });

/** Количество препарата — НЕ деньги: точность источника, без дорисованных нулей. */
export function formatQty(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return QTY.format(value);
}

/**
 * Цены за единицу: до трёх разных — через «·», больше — средняя с пометкой.
 * Нет цен — прочерк, а не ноль: «цены нет» и «цена ноль» — разные утверждения.
 */
export function pricesLabel(prices: Prices | null | undefined): string {
  if (!prices) return '—';
  if (prices.many && prices.avg !== null) return `ср. ${formatRub(prices.avg)}`;
  if (prices.distinct.length === 0) {
    return prices.avg !== null ? formatRub(prices.avg) : '—';
  }
  return prices.distinct.map((p) => formatRub(p.price)).join(' · ');
}

/** «2026-10-14…» → «14.10.2026». Мусор — прочерк. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '—';
}

/** Контакт одной строкой: «Иванов И. И. · +7 900 000-00-01». */
export function contactLine(list: CardContact[]): string {
  if (list.length === 0) return '—';
  const c = list[0];
  return [c.name, c.phones[0]].filter(Boolean).join(' · ');
}

/** Дата N дней назад в ISO (YYYY-MM-DD) — фильтр «период» визитов. */
export function daysAgoIso(days: number, now: Date): string {
  const d = new Date(now.getTime() - days * 86_400_000);
  return d.toISOString().slice(0, 10);
}

/** «Что нашли» у осмотра из веба: проблемы → заметка → фаза. */
export function webInspectionFinding(ai: Record<string, unknown> | null | undefined): string {
  if (!ai) return '—';
  const problems = Array.isArray(ai.problems)
    ? (ai.problems as { name?: unknown }[])
        .map((p) => (typeof p?.name === 'string' ? p.name : ''))
        .filter(Boolean)
    : [];
  if (problems.length) return problems.join(', ');
  if (typeof ai.note === 'string' && ai.note.trim()) return ai.note.trim();
  if (typeof ai.phase === 'string' && ai.phase.trim()) return ai.phase.trim();
  return 'Без отклонений';
}
